"use client";

/**
 * Landing footer — balanced 4-zone layout.
 * Left rail: brand + contact. Top zone: 3 balanced link columns (h3 headings
 * under sr-only h2 for heading-order compliance). Bottom zone: sub-footer
 * strip with disclosure, social, legal.
 * a11y: heading order compliant (h1→h2→h3), link contrast AA on white bg.
 */
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import { useT } from "@/lib/locale";
import { BRAND } from "@petci/shared-ui";
import LatestStrip from "./LatestStrip";

const QUICK_LINKS = [
  { labelKey: "footer.linkPayBill", href: "/bill" },
  { labelKey: "footer.linkReportOutage", href: "/report" },
  { labelKey: "footer.linkOutageMap", href: "/outage/map" },
  { labelKey: "footer.linkAskAssistant", href: "/support" },
  { labelKey: "footer.linkSignIn", href: "/sign-in" },
];

const FORMS = [
  { labelKey: "footer.linkApplyService", href: "/services" },
  { labelKey: "footer.linkStopService", href: "/services/start-stop-transfer/type" },
  { labelKey: "footer.linkDamageClaim", href: "/services/damage-claims" },
  { labelKey: "footer.linkStreetlight", href: "/report/streetlight" },
];

// CORE / UORE / EV charging have no in-app program page in the POC; the landing
// dropped its #programs section in the May redesign but these footer anchors
// survived and scrolled nowhere (L3.4). Retargeted to the real, live Pelican
// program pages (verified on pelicanenergytci.com). Hurricane Prep is a real
// in-page section (#hurricane), so it stays an internal anchor.
const PROGRAMS_LINKS = [
  { labelKey: "footer.linkCore", href: "https://www.pelicanenergytci.com/core", external: true },
  { labelKey: "footer.linkUore", href: "https://www.pelicanenergytci.com/uore", external: true },
  { labelKey: "footer.linkHurricanePrep", href: "#hurricane", external: false },
  { labelKey: "footer.linkEvCharging", href: "https://www.pelicanenergytci.com/evcharging", external: true },
];

const SOCIAL_LINKS = [
  { label: "Facebook", href: "https://www.facebook.com/PelicanEnergyTCI/" },
  { label: "X", href: "https://x.com/PelicanEnergy" },
  { label: "YouTube", href: "https://www.youtube.com/@pelicanenergytci" },
  { label: "Instagram", href: "https://www.instagram.com/pelicanenergytci/" },
];

export default function LandingFooter() {
  const t = useT();

  return (
    <>
      <LatestStrip />
      <footer className="border-t border-neutral-200 bg-white py-10 sm:py-12">
        <div className="max-w-6xl 2xl:max-w-[1408px] mx-auto px-4 sm:px-6">
          {/* sr-only landmark heading for heading-order compliance */}
          <h2 className="sr-only">{t("footer.siteLinks")}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Left rail — Brand + Contact */}
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-7 h-7 rounded-full bg-brand-primary-hover flex items-center justify-center" aria-hidden="true">
                  <span className="text-white font-display font-bold text-xs">PE</span>
                </div>
                <span className="font-display font-semibold text-neutral-900 text-sm">
                  {BRAND.full}
                </span>
              </div>
              <p className="text-xs text-neutral-600 leading-relaxed mb-4">
                {t("footer.initiative")}
              </p>
              <ul className="flex flex-col gap-1.5 text-sm text-neutral-600">
                <li>(649) 946-4313</li>
                <li className="break-all">customerservice@pelicanenergytci.com</li>
                <li className="text-xs text-neutral-600 leading-relaxed">
                  P.O. Box 132, 1030 Leeward Highway, Providenciales, TKCA 1ZZ
                </li>
                <li>
                  <a
                    href="https://www.pelicanenergytci.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary-hover hover:underline"
                  >
                    pelicanenergytci.com →
                  </a>
                </li>
              </ul>
            </div>

            {/* Col 2 — Quick Links */}
            <div>
              <h3 className="font-display font-semibold text-neutral-900 text-sm mb-3">
                {t("footer.quicklinks")}
              </h3>
              <ul className="flex flex-col gap-2">
                {QUICK_LINKS.map(({ labelKey, href }) => (
                  <li key={labelKey}>
                    <Link
                      href={href}
                      className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                    >
                      {t(labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3 — Customer Forms */}
            <div>
              <h3 className="font-display font-semibold text-neutral-900 text-sm mb-3">
                {t("footer.forms")}
              </h3>
              <ul className="flex flex-col gap-2">
                {FORMS.map(({ labelKey, href }) => (
                  <li key={labelKey}>
                    <Link
                      href={href}
                      className="text-sm text-neutral-600 hover:text-brand-primary transition-colors"
                    >
                      {t(labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 4 — Programs */}
            <div>
              <h3 className="font-display font-semibold text-neutral-900 text-sm mb-3">
                {t("footer.programs")}
              </h3>
              <ul className="flex flex-col gap-2">
                {PROGRAMS_LINKS.map(({ labelKey, href, external }) => (
                  <li key={labelKey}>
                    <a
                      href={href}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-sm text-neutral-600 hover:text-brand-primary transition-colors inline-flex items-center gap-1"
                    >
                      {t(labelKey)}
                      {external && <ExternalLink size={11} aria-hidden />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sub-footer strip — disclosure + social + legal */}
          <div className="mt-10 pt-6 border-t border-neutral-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 text-xs text-neutral-600">
                <span className="bg-brand-accent/20 text-neutral-700 font-semibold px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider">
                  {t("footer.previewBadge")}
                </span>
                <span>{t("footer.disclaimer")}</span>
              </div>
              <div className="flex items-center gap-4">
                {SOCIAL_LINKS.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-neutral-600 hover:text-brand-primary-hover transition-colors"
                    aria-label={`${label} (opens in new window)`}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-4 text-xs text-neutral-600">
                <Link href="/accessibility" className="hover:text-brand-primary-hover transition-colors">
                  {t("footer.accessibility")}
                </Link>
                <span>{t("footer.privacy")}</span>
              </div>
              <p className="text-xs text-neutral-600">
                {t("footer.subfooter")}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
